import React from 'react';
import { PaymentTypeDataHooks } from './dataHooks';
import {
  TFunction,
  useEnvironment,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { classes, st } from './PaymentTypeSelection.st.css';
import { useFormActions } from '../../../../Hooks/useFormActions';
import { Dropdown } from 'wix-ui-tpa/cssVars';
import { useSettings } from '@wix/tpa-settings/react';
import { getContent } from '../../../../../../utils/content/content';
import settingsParams from '../../../../settingsParams';
import { ISettingsContextValue } from '@wix/tpa-settings';
import Text from '../../../../Text/Text';
import { TagName } from '../../../../../../utils/tagName.const';
import { SlotService } from '../../../../../../utils/state/types';
import { getServiceSlotIdentifier } from '../../../../../../utils';
import { PaymentMethod } from '../../../../../../types/types';

export type PaymentTypeSelectionProps = {
  selectedPaymentType: PaymentMethod;
  paymentTypes: PaymentMethod[];
  service: SlotService;
};

export const PaymentTypeSelection: React.FC<PaymentTypeSelectionProps> = ({
  selectedPaymentType,
  paymentTypes,
  service,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();

  const settings = useSettings();
  const { setSelectedPaymentType } = useFormActions();
  const dropdownOptions = paymentTypes
    .filter((paymentType) =>
      [PaymentMethod.OFFLINE, PaymentMethod.ONLINE].includes(paymentType),
    )
    .map((paymentType) => ({
      id: paymentType,
      value: mapTypeToDropdownValue(paymentType, t, settings),
      isSelectable: true,
    }));

  const componentKey = getServiceSlotIdentifier(service.nestedSlot);

  return (
    <div
      className={st(classes.root, { isMobile })}
      data-hook={`${PaymentTypeDataHooks.Payment_Type_Selection}_${componentKey}`}
    >
      <Text
        className={classes.label}
        data-hook={`${PaymentTypeDataHooks.Payment_Type_Selector_Label}_${componentKey}`}
        tagName={TagName.Span}
      >
        {getContent({
          settings,
          settingsParam: settingsParams.selectPaymentTypeLabel,
        })}
      </Text>
      <Dropdown
        className={classes.PaymentTypeSelector}
        initialSelectedId={selectedPaymentType}
        options={dropdownOptions}
        onChange={(option) =>
          setSelectedPaymentType(service, option.id as PaymentMethod)
        }
        data-hook={`${PaymentTypeDataHooks.Dropdown}_${componentKey}`}
      />
    </div>
  );
};

const mapTypeToDropdownValue = (
  type: PaymentMethod,
  t: TFunction,
  settings: Partial<ISettingsContextValue>,
): string => {
  const settingsParam =
    type === PaymentMethod.ONLINE
      ? settingsParams.payOnlineText
      : settingsParams.payOfflineText;
  return getContent({
    settings,
    settingsParam,
  });
};
